import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, supabaseAdmin } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'

interface UserProfile {
    id: string
    agency_id: string
    role: 'superadmin' | 'owner' | 'admin' | 'agent' | 'proprietari'
    full_name: string
    email: string
    slug?: string
    agency_name?: string
    modules_config?: any
}

interface AuthContextValue {
    user: User | null
    profile: UserProfile | null
    isLoading: boolean
    isSuperAdmin: boolean
    isAdmin: boolean
    signIn: (email: string, password: string) => Promise<{ error: string | null }>
    signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    profile: null,
    isLoading: true,
    isSuperAdmin: false,
    isAdmin: false,
    signIn: async () => ({ error: null }),
    signOut: async () => { },
})

export function useAuth() { return useContext(AuthContext) }

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const fetchProfile = async (u: User | null) => {
        if (!u) {
            setProfile(null)
            return
        }

        // Lógica para permitir al SuperAdmin entrar en el entorno de cualquier agencia (Impersonate)
        const applyImpersonation = async (prof: any) => {
            if (prof.role === 'superadmin') {
                const searchParams = new URLSearchParams(window.location.search)
                const tenant = searchParams.get('tenant')

                if (tenant) {
                    const { data: agency } = await supabaseAdmin
                        .from('agencies')
                        .select('id, name, slug, modules_config')
                        .eq('slug', tenant)
                        .maybeSingle()
                    
                    if (agency) {
                        prof.agency_id = agency.id
                        prof.role = 'superadmin' // Mantener superadmin para tener todos los poderes
                        prof.full_name = `${prof.full_name} (SA)`
                        prof.slug = agency.slug
                        prof.agency_name = agency.name
                        prof.modules_config = agency.modules_config
                        return prof as UserProfile
                    }
                }

                const targetAgency = localStorage.getItem('sa_impersonate_agency_id')
                const targetAgencySlug = localStorage.getItem('sa_impersonate_agency_slug')
                const targetAgencyName = localStorage.getItem('sa_impersonate_agency_name')
                const targetAdminName = localStorage.getItem('sa_impersonate_admin_name')
                if (targetAgency) {
                    prof.agency_id = targetAgency
                    prof.role = 'superadmin' // Mantener superadmin
                    if (targetAdminName) prof.full_name = `${targetAdminName} (SA)`
                    if (targetAgencySlug) prof.slug = targetAgencySlug
                    if (targetAgencyName) prof.agency_name = targetAgencyName
                    
                    // Cargar modules_config para la agencia impersonada
                    const { data: agData } = await supabaseAdmin
                        .from('agencies')
                        .select('modules_config')
                        .eq('id', targetAgency)
                        .maybeSingle()
                    if (agData) {
                        prof.modules_config = agData.modules_config
                    }
                }
            } else if (prof.agencies) {
                // Flatten joined data
                prof.slug = prof.agencies.slug
                prof.agency_name = prof.agencies.name
                prof.modules_config = prof.agencies.modules_config
            }
            return prof as UserProfile
        }

        // 1. Intentamos leer de la tabla agency_users con join a agencies
        const { data, error } = await supabase
            .from('agency_users')
            .select('*, agencies(slug, name, modules_config)')
            .eq('user_id', u.id)
            .single()

        if (data) {
            setProfile(await applyImpersonation(data as Partial<UserProfile>))
            return
        }

        // 2. Fallback: Si falla la BD, usamos el JWT directamente (no depende de RLS)
        if (error) {
            console.warn('[Auth] No se pudo leer agency_users, usando JWT fallback:', error.message)
            const jwtRole = u.user_metadata?.role as UserProfile['role'] | undefined
            if (jwtRole) {
                const rawProfile = {
                    id: u.id,
                    email: u.email ?? '',
                    role: jwtRole,
                    full_name: u.user_metadata?.full_name ?? u.email ?? '',
                    agency_id: u.user_metadata?.agency_id ?? '',
                }
                setProfile(await applyImpersonation(rawProfile))
                return
            }
        }

        setProfile(null)
    }

    useEffect(() => {
        const init = async () => {
            try {
                const { data, error } = await supabase.auth.getSession()
                
                if (error) {
                    console.warn('[Auth] Error recuperando sesión:', error)
                    // Si falla el refresh token (error 400 o refresh_token_not_found), 
                    // limpiamos el estado local para evitar bucles de error.
                    const isAuthError = (error as any).__isAuthError || error.name === 'AuthApiError'
                    const isRefreshError = error.message?.includes('refresh_token_not_found') || (error as any).code === 'refresh_token_not_found' || (error as any).status === 400
                    
                    if (isAuthError && isRefreshError) {
                        try {
                            await supabase.auth.signOut()
                        } catch (e) {
                            // Fallback if signOut fails
                        }
                        // Forzar borrado de storage local por si acaso Supabase se atasca
                        for (let i = 0; i < localStorage.length; i++) {
                            const key = localStorage.key(i)
                            if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
                                localStorage.removeItem(key)
                            }
                        }
                        
                        // Solo redirigir si estamos en una ruta protegida
                        const path = window.location.pathname
                        if (path.startsWith('/admin') || path.startsWith('/superadmin')) {
                            window.location.href = '/login'
                        } else {
                            // En la web pública, simplemente reiniciamos el estado
                            setUser(null)
                            setProfile(null)
                        }
                    }
                }

                const u = data.session?.user ?? null
                setUser(u)
                await fetchProfile(u)
            } catch (err) {
                console.error('[Auth] Error crítico en inicialización de sesión:', err)
            } finally {
                setIsLoading(false)
            }
        }

        init()
        
        // Listener para cambios de URL (importante para el tenant)
        const handleLocationChange = async () => {
            const { data: { user } } = await supabase.auth.getUser()
            fetchProfile(user)
        }
        window.addEventListener('popstate', handleLocationChange)
        window.addEventListener('pushState', handleLocationChange) // Custom event if needed or use a hook

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
            const u = session?.user ?? null
            setUser(u)
            fetchProfile(u)
        })

        return () => {
            subscription.unsubscribe()
            window.removeEventListener('popstate', handleLocationChange)
        }
    }, [])

    const signIn = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        return { error: error?.message ?? null }
    }

    const signOut = async () => {
        await supabase.auth.signOut()
        setProfile(null)
        setUser(null)
    }

    const isSuperAdmin = profile?.role === 'superadmin'
    const isAdmin = profile?.role === 'admin' || profile?.role === 'owner' || profile?.role === 'superadmin'

    return (
        <AuthContext.Provider value={{ user, profile, isLoading, isSuperAdmin, isAdmin, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    )
}
